/** Shapes the purchasing screens read. Mirrors `server/src/services/demand.ts`. */

import type { TagTone } from '../components/primitives.js'

export type DemandStatus = 'covered' | 'order' | 'ordered' | 'ready' | 'short'

export interface DemandRow {
  itemId: string
  itemName: string
  sku: string | null
  unit: string
  categoryId: string | null
  categoryName: string | null
  yardId: string
  yardName: string
  planned: number
  onHand: number
  reservedOther: number
  available: number
  onOrder: number
  shortfall: number
  status: DemandStatus
  vendorId: string | null
  vendorName: string | null
  purchaseOrderId: string | null
  purchaseOrderRef: string | null
  purchaseOrderStatus: string | null
  expectedOn: string | null
  visits: {
    id: string; reference: string; title: string; date: string; qty: number
    /** The visit and its material line — swapping lives on the visit's line (`docs/visit-page.md` §9). */
    workItemId: string; workItemReference: string | null; materialLineId: string | null
  }[]
  neededBy: string | null
}

export interface DemandResponse {
  window: { from: string; to: string; days: number }
  items: DemandRow[]
  totals: Record<DemandStatus, number> & { total: number }
}

export interface PurchaseOrderRow {
  id: string
  reference: string
  status: string
  how: 'pickup' | 'delivery'
  expectedOn: string | null
  windowNote: string | null
  willCallRef: string | null
  placedAt: string | null
  collectedAt: string | null
  vendorId: string
  vendorName: string
  vendorCity: string | null
  terms: string | null
  paymentMethod: string
  counterHours: string | null
  yardName: string
  assignedUserName: string | null
  assignedCrewName: string | null
  receiptCount: number
  verifiedCount: number
  itemsSummary: string | null
  forJobs: string | null
  /** Each line; `buy*` set where it was ordered in the vendor's unit (catalog §8). */
  lines?: PurchaseOrderLine[]
}

export interface PurchaseOrderLine {
  name: string
  qty: number
  unit: string
  unitCost: number
  buyQty: number | null
  buyUnit: string | null
  buyUnitName: string | null
  buyPrice: number | null
  buyPer: number | null
}

export interface PurchaseOrderResponse {
  tab: string
  items: PurchaseOrderRow[]
  counts: Record<string, number>
}

export interface CloseoutLine {
  itemId: string
  itemName: string
  unit: string
  yardId: string
  qtyLoaded: number
  qtyUsed: number
  qtyWasted: number
  qtyReturned: number
  note: string | null
  addedByCrew: boolean
  swappedFromName: string | null
}

export interface CloseoutSheet {
  closeoutId: string | null
  status: string | null
  lines: CloseoutLine[]
  fieldReceipts: {
    id: string
    vendorName: string
    amountCents: number
    description: string | null
    paidBy: string
    purchasedAt: string
  }[]
}

/** The badge each demand status wears, and what it means in one word. */
export const DEMAND_STATUS: Record<DemandStatus, {
  label: string
  /** A `Tag` tone, not classes: see the note on `Tag`. */
  tone: TagTone
  dot: string
}> = {
  covered: { label: 'Covered', tone: 'ok', dot: 'bg-accent' },
  order: { label: 'Order', tone: 'warn', dot: 'bg-warn' },
  ordered: { label: 'Ordered', tone: 'info', dot: 'bg-info' },
  ready: { label: 'Ready', tone: 'ok', dot: 'bg-accent' },
  short: { label: 'Short', tone: 'bad', dot: 'bg-danger' },
}
